import { createSelector } from 'reselect'
import {
  getTourV2IdForCheckoutItem,
  isLuxPlusSubscriptionItem,
  isTourV2ExperienceItem,
  isSubscriptionJoinItem,
  isTourV2Item,
  isNoProtectionInsuranceItem,
  isInsuranceItem,
} from 'lib/checkout/checkoutUtils'
import { subDays } from 'lib/datetime/dateUtils'
import config from 'constants/config'
import { PAYMENT_SCHEDULE_ENABLED_REGIONS } from 'constants/config/region'
import { getTourV2CheckoutItems } from 'checkout/selectors/view/accommodation'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { sum } from 'lib/array/arrayUtils'

export const isPaymentScheduleEnabled = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (_state: App.State) => config.PAYMENT_SCHEDULE_ENABLED,
  (currentRegionCode, isEnabled) => {
    if (!isEnabled) {
      return false
    }

    if (!PAYMENT_SCHEDULE_ENABLED_REGIONS.includes(currentRegionCode)) {
      return false
    }

    return true
  },
)

const isItemAllowedWithPaymentSchedules = (item: App.Checkout.AnyItem): boolean => {
  return isTourV2Item(item) ||
  isTourV2ExperienceItem(item) ||
  isLuxPlusSubscriptionItem(item) ||
  isSubscriptionJoinItem(item) ||
  isInsuranceItem(item)
}

const canGetItemsPaymentScheduleInfo = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.tourV2Offers,
  (state: App.State) => state.checkout.cart.isGift,
  (items, tourV2Offers, isGift): Array<boolean> | false => {
    if (isGift || !items.every(isItemAllowedWithPaymentSchedules)) {
      return false
    }

    const tourV2Items = items.filter(isTourV2Item)

    return tourV2Items.map((item) => {
      const offer = tourV2Offers[getTourV2IdForCheckoutItem(item)]
      const templateId = offer?.paymentScheduleInfo?.templateId
      if (!templateId) {
        return false
      }
      return true
    })
  },
)

const isAnyItemDisabledForPaymentSchedule = createSelector(
  canGetItemsPaymentScheduleInfo,
  (itemsPaymentScheduleInfo): boolean => !itemsPaymentScheduleInfo || itemsPaymentScheduleInfo.some(item => !item),
)

export const isPaymentScheduleAvailable = createSelector(
  isPaymentScheduleEnabled,
  (state: App.State) => state.checkout.cart.postPurchase,
  isAnyItemDisabledForPaymentSchedule,
  (
    isPaymentScheduleEnabled,
    postPurchase,
    isAnyItemDisabledForPaymentSchedule,
  ): boolean => {
    return (
      isPaymentScheduleEnabled &&
      !postPurchase &&
      !isAnyItemDisabledForPaymentSchedule
    )
  },
)

export const getPaymentScheduleTemplateIdFromTourV2Offer = createSelector(
  (state: App.State) => state.offer.tourV2Offers,
  getTourV2CheckoutItems,
  (tourV2Offers, tourV2Items): string | undefined | null => {
    const item = tourV2Items.find(item => {
      const offer = tourV2Offers[getTourV2IdForCheckoutItem(item)]
      return offer?.paymentScheduleInfo?.templateId
    })

    if (!item) {
      return undefined
    }

    return tourV2Offers[getTourV2IdForCheckoutItem(item)]?.paymentScheduleInfo?.templateId
  },
)

const mapItemType = (item: App.Checkout.AnyItem): string => {
  if (isTourV2Item(item)) {
    return 'tour_v2'
  }
  if (isTourV2ExperienceItem(item)) {
    return 'tour_optional_experience'
  }
  if (isLuxPlusSubscriptionItem(item) || isSubscriptionJoinItem(item)) {
    return 'subscription'
  }
  return item.itemType
}

export const getPaymentScheduleRequestInfo = createSelector(
  isPaymentScheduleAvailable,
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.tourV2Offers,
  (state: App.State) => state.checkout.promo?.items,
  (isPaymentScheduleAvailable, cartItems, tourV2Offers, promoItems): App.Checkout.PaymentScheduleRequestInfo | null => {
    if (!isPaymentScheduleAvailable) {
      return null
    }

    const getPaymentScheduleInfoFromOffer = (item: App.Checkout.TourV2Item) => tourV2Offers[getTourV2IdForCheckoutItem(item)]?.paymentScheduleInfo

    const tourV2Items = cartItems.filter(isTourV2Item)

    const itemWithTemplateId = tourV2Items.find(getPaymentScheduleInfoFromOffer)
    if (!itemWithTemplateId) {
      return null
    }

    const paymentScheduleInfo = getPaymentScheduleInfoFromOffer(itemWithTemplateId)
    if (!paymentScheduleInfo?.templateId) {
      return null
    }

    if (!paymentScheduleInfo.supplierPaymentNumberOfDays) {
      return null
    }

    if (!itemWithTemplateId.total) {
      return null
    }

    const dueDate = subDays(new Date(itemWithTemplateId.startDate), paymentScheduleInfo.supplierPaymentNumberOfDays)

    const items = cartItems
      .filter(item => !(isSubscriptionJoinItem(item) && item.waived))
      .filter(item => !isNoProtectionInsuranceItem(item))
      .map((item) => {
        let total = 0
        if (isLuxPlusSubscriptionItem(item) || isSubscriptionJoinItem(item)) {
          total = item.amount ?? 0
        } else {
          total = Math.min(...[item.memberTotal, item.total].filter((val): val is number => val !== undefined && val > 0)) || 0
        }
        const discountTotal = promoItems?.find(promoItem => promoItem.itemId === item.itemId)?.discountAmount ?? 0
        return {
          idItems: item.itemId,
          total,
          type: mapItemType(item),
          discountTotal,
        }
      })

    return {
      templateId: paymentScheduleInfo.templateId,
      bookingInfo: {
        bookingDate: moment().format(ISO_DATE_FORMAT),
        checkinDate: itemWithTemplateId.startDate,
        supplierDueDate: moment(dueDate).format(ISO_DATE_FORMAT),
        items,
      },
    }
  },
)

export const getServiceFeeForInitialPayment = createSelector(
  (state: App.State) => state.paymentSchedule,
  (paymentSchedule): number => {
    if (!paymentSchedule.eligible || !paymentSchedule.payment_schedules) {
      return 0
    }
    return paymentSchedule.payment_schedules[0].feeAmount || 0
  },
)

export const getPaymentScheduleTotalServiceFee = createSelector(
  (state: App.State) => state.paymentSchedule,
  (paymentSchedule): number => {
    if (!paymentSchedule.eligible || !paymentSchedule.payment_schedules) {
      return 0
    }
    return sum(paymentSchedule.payment_schedules, (schedule) => schedule.feeAmount)
  },
)

export const getServiceFeeForLaterPayments = createSelector(
  getPaymentScheduleTotalServiceFee,
  getServiceFeeForInitialPayment,
  (totalServiceFee, initialServiceFee): number => {
    return totalServiceFee - initialServiceFee
  },
)

export const getPaymentScheduleTimelineData = createSelector(
  (state: App.State) => state.paymentSchedule,
  (paymentSchedule): Array<App.PaymentScheduleTimelineData> => {
    return paymentSchedule.payment_schedules.slice(1).map((schedule) => ({
      dueDate: schedule.dueDate,
      totalAmount: schedule.totalAmount,
      feeAmount: schedule.feeAmount,
    }))
  },
)

export const getServiceFeePaymentScheduleIndex = createSelector(
  (state: App.State) => state.paymentSchedule,
  (paymentSchedule): number => {
    return paymentSchedule.payment_schedules.findIndex(schedule => schedule.feeAmount > 0)
  },
)
